/** Klantteksten op de productpagina. Pretty Orange DE is Duits, Pretty Orange FR is Frans, de rest blijft Nederlands. */

export type Locale = 'nl' | 'de' | 'fr';

export function localeForShop(shopId: string | undefined): Locale {
  if (shopId === 'po-de') return 'de';
  if (shopId === 'po-fr') return 'fr';
  return 'nl';
}

export interface UiCopy {
  titles: {
    intro: string;
    preparing: string;
    style: string;
    scenes: string;
    working: string;
    compare: string;
    applying: string;
    done: string;
    error: string;
  };
  cancel: string;
  close: string;
  libraryLabel: string;
  libraryTitle: string;
  libraryNote: string;
  exampleAlt: (label: string) => string;
  tipsHeading: string;
  tipSharp: string;
  tipSubject: string;
  tipFilters: string;
  privacy: string;
  formats: string;
  choosePhoto: string;
  otherPhoto: string;
  readingPhoto: string;
  placing: string;
  workMessages: readonly string[];
  sendingPhoto: string;
  percent: string;
  slow: string;
  usual: string;
  compareHint: string;
  again: string;
  apply: string;
  placed: string;
  cardAlt: string;
  editHint: string;
  edit: string;
  tryOther: string;
  ok: string;
  chooseOtherPhoto: string;
  retry: string;
  pickOther: string;
  compareAria: string;
  photoVisible: (pct: number) => string;
  resultAlt: string;
  originalAlt: string;
  photoChip: string;
  chosenAlt: string;
  yourPhotoAlt: string;
  heroAria: string;
  ctaApplied: string;
  ctaScenes: string;
  ctaStyles: string;
  badgeDone: string;
  badgeNew: string;
  ctaSubApplied: string;
  ctaSub: string;
}

const NL: UiCopy = {
  titles: {
    intro: 'Kies je foto',
    preparing: 'Foto inlezen',
    style: 'Kies een stijl',
    scenes: 'Kies een scène',
    working: 'Afbeelding maken',
    compare: 'Jouw afbeelding',
    applying: 'In je ontwerp zetten',
    done: 'Klaar!',
    error: 'Er ging iets mis',
  },
  cancel: 'Annuleren',
  close: 'Sluiten',
  libraryLabel: 'Eerder gemaakt',
  libraryTitle: 'Eerder door jou gemaakt',
  libraryNote: 'Dit is een afbeelding die je zelf eerder maakte. Het voorbeeld hierboven blijft een voorbeeld.',
  exampleAlt: (label) => `Voorbeeld: ${label}`,
  tipsHeading: 'Tips voor een beter resultaat',
  tipSharp: 'Een scherpe foto met goed licht',
  tipSubject: 'Het onderwerp goed zichtbaar, niet te klein in beeld',
  tipFilters: 'Liefst zonder zware filters of tekst',
  privacy: 'We gebruiken je foto alleen om je afbeelding te maken.',
  formats: 'JPEG, PNG, WebP of HEIC/AVIF · maximaal 10 MB',
  choosePhoto: 'Kies een foto',
  otherPhoto: 'Andere foto',
  readingPhoto: 'Foto inlezen…',
  placing: 'We zetten je afbeelding in het ontwerp…',
  workMessages: ['We zetten je foto om…', 'De stijl wordt aangebracht…', 'Details bijwerken…', 'De laatste details…'],
  sendingPhoto: 'Foto versturen…',
  percent: 'procent',
  slow: 'Het duurt wat langer dan normaal. Laat deze pagina open, dan maken we hem af.',
  usual: 'Dit duurt meestal 10 tot 30 seconden.',
  compareHint: 'Schuif om je foto en jouw afbeelding te vergelijken. Het voorbeeld blijft alleen een voorbeeld.',
  again: 'Opnieuw',
  apply: 'Zet in mijn ontwerp',
  placed: 'Je afbeelding staat in je ontwerp',
  cardAlt: 'Voorbeeld van je ontwerp',
  editHint: 'Je kunt het ontwerp nu verder aanpassen.',
  edit: 'Bewerken',
  tryOther: 'Andere foto proberen',
  ok: 'Oké',
  chooseOtherPhoto: 'Kies een andere foto',
  retry: 'Probeer opnieuw',
  pickOther: 'Andere foto kiezen',
  compareAria: 'Vergelijk foto en afbeelding',
  photoVisible: (pct) => `${pct}% foto zichtbaar`,
  resultAlt: 'Je foto als afbeelding',
  originalAlt: 'Je originele foto',
  photoChip: 'Foto',
  chosenAlt: 'Je gekozen foto',
  yourPhotoAlt: 'Je foto',
  heroAria: 'Een foto wordt een aquarel',
  ctaApplied: 'Afbeelding staat in je ontwerp',
  ctaScenes: 'Je eigen foto in je ontwerp',
  ctaStyles: 'Maak een afbeelding van je foto',
  badgeDone: 'Klaar',
  badgeNew: 'Nieuw',
  ctaSubApplied: 'Tik om een andere foto te proberen',
  ctaSub: 'Upload je foto en maak iets echt persoonlijks.',
};

const DE: UiCopy = {
  titles: {
    intro: 'Wähle dein Foto',
    preparing: 'Foto wird geladen',
    style: 'Wähle einen Stil',
    scenes: 'Wähle eine Szene',
    working: 'Bild wird erstellt',
    compare: 'Dein Bild',
    applying: 'Wird in dein Design gesetzt',
    done: 'Fertig!',
    error: 'Da ist etwas schiefgelaufen',
  },
  cancel: 'Abbrechen',
  close: 'Schließen',
  libraryLabel: 'Zuvor erstellt',
  libraryTitle: 'Von dir erstellt',
  libraryNote: 'Das ist ein Bild, das du selbst erstellt hast. Das Beispiel oben bleibt ein Beispiel.',
  exampleAlt: (label) => `Beispiel: ${label}`,
  tipsHeading: 'Tipps für ein besseres Ergebnis',
  tipSharp: 'Ein scharfes Foto mit gutem Licht',
  tipSubject: 'Das Motiv gut sichtbar, nicht zu klein im Bild',
  tipFilters: 'Am besten ohne starke Filter oder Text',
  privacy: 'Wir verwenden dein Foto nur, um dein Bild zu erstellen.',
  formats: 'JPEG, PNG, WebP oder HEIC/AVIF · maximal 10 MB',
  choosePhoto: 'Foto auswählen',
  otherPhoto: 'Anderes Foto',
  readingPhoto: 'Foto wird gelesen…',
  placing: 'Wir setzen dein Bild in das Design…',
  workMessages: ['Wir wandeln dein Foto um…', 'Der Stil wird angewendet…', 'Details werden angepasst…', 'Die letzten Details…'],
  sendingPhoto: 'Foto wird gesendet…',
  percent: 'Prozent',
  slow: 'Das dauert etwas länger als gewöhnlich. Lass diese Seite offen, dann schließen wir es ab.',
  usual: 'Das dauert meist 10 bis 30 Sekunden.',
  compareHint: 'Schiebe, um dein Foto und dein Bild zu vergleichen. Das Beispiel bleibt nur ein Beispiel.',
  again: 'Nochmal',
  apply: 'In mein Design setzen',
  placed: 'Dein Bild ist in deinem Design',
  cardAlt: 'Vorschau deines Designs',
  editHint: 'Du kannst das Design jetzt weiter anpassen.',
  edit: 'Bearbeiten',
  tryOther: 'Anderes Foto versuchen',
  ok: 'Okay',
  chooseOtherPhoto: 'Wähle ein anderes Foto',
  retry: 'Nochmal versuchen',
  pickOther: 'Anderes Foto wählen',
  compareAria: 'Vergleiche Foto und Bild',
  photoVisible: (pct) => `${pct} % Foto sichtbar`,
  resultAlt: 'Dein Foto als Bild',
  originalAlt: 'Dein Originalfoto',
  photoChip: 'Foto',
  chosenAlt: 'Dein ausgewähltes Foto',
  yourPhotoAlt: 'Dein Foto',
  heroAria: 'Aus einem Foto wird eine Illustration',
  ctaApplied: 'Bild ist in deinem Design',
  ctaScenes: 'Dein eigenes Foto in deinem Design',
  ctaStyles: 'Erstelle ein Bild aus deinem Foto',
  badgeDone: 'Fertig',
  badgeNew: 'Neu',
  ctaSubApplied: 'Tippe, um ein anderes Foto auszuprobieren',
  ctaSub: 'Lade dein Foto hoch und mach etwas ganz Persönliches.',
};

const FR: UiCopy = {
  titles: {
    intro: 'Choisissez votre photo',
    preparing: 'Lecture de la photo',
    style: 'Choisissez un style',
    scenes: 'Choisissez une scène',
    working: 'Création de l\'image',
    compare: 'Votre image',
    applying: 'Ajout à votre création',
    done: 'C\'est prêt !',
    error: 'Une erreur s\'est produite',
  },
  cancel: 'Annuler',
  close: 'Fermer',
  libraryLabel: 'Déjà créées',
  libraryTitle: 'Vos créations',
  libraryNote: 'Cette image a été créée précédemment par vos soins. L\'image ci-dessus est uniquement un exemple.',
  exampleAlt: (label) => `Exemple : ${label}`,
  tipsHeading: 'Conseils pour un meilleur résultat',
  tipSharp: 'Une photo nette et bien éclairée.',
  tipSubject: 'Le sujet bien visible, pas trop petit dans l\'image',
  tipFilters: 'De préférence sans filtre ni texte',
  privacy: 'Nous utilisons votre photo uniquement pour créer votre image.',
  formats: 'JPEG, PNG, WebP ou HEIC/AVIF · 10 Mo maximum',
  choosePhoto: 'Choisissez une photo',
  otherPhoto: 'Autre photo',
  readingPhoto: 'Lecture de la photo…',
  placing: 'Nous ajoutons votre image à la création…',
  workMessages: ['Nous transformons votre photo…', 'Le style est appliqué…', 'Ajustement des détails…', 'Les derniers détails…'],
  sendingPhoto: 'Envoi de la photo…',
  percent: 'pour cent',
  slow: 'Cela prend un peu plus de temps que d\'habitude. Laissez cette page ouverte, nous terminons de générer l\'image.',
  usual: 'Cela prend en général 10 à 30 secondes.',
  compareHint: 'Faites glisser le curseur pour comparer votre photo avec l\'image proposée. Le résultat affiché est uniquement un aperçu.',
  again: 'Recommencer',
  apply: 'Mettre dans ma création',
  placed: 'Votre image est dans votre création',
  cardAlt: 'Aperçu de votre création',
  editHint: 'Vous pouvez maintenant continuer à modifier la création.',
  edit: 'Modifier',
  tryOther: 'Essayer une autre photo',
  ok: 'OK',
  chooseOtherPhoto: 'Choisir une autre photo',
  retry: 'Veuillez réessayer.',
  pickOther: 'Choisir une autre photo',
  compareAria: 'Comparez la photo et l\'image',
  photoVisible: (pct) => `${pct} % de la photo visible`,
  resultAlt: 'Votre photo en image',
  originalAlt: 'Votre photo d\'origine',
  photoChip: 'Photo',
  chosenAlt: 'Votre photo choisie',
  yourPhotoAlt: 'Votre photo',
  heroAria: 'Une photo devient une illustration',
  ctaApplied: 'L\'image est dans votre création',
  ctaScenes: 'Votre propre photo dans votre création',
  ctaStyles: 'Créez une image à partir de votre photo',
  badgeDone: 'Prêt',
  badgeNew: 'Nouveau',
  ctaSubApplied: 'Essayez une autre photo',
  ctaSub: 'Importez votre photo et créez quelque chose de vraiment personnel.',
};

const COPY: Record<Locale, UiCopy> = { nl: NL, de: DE, fr: FR };

export function uiCopy(shopId: string | undefined): UiCopy {
  return COPY[localeForShop(shopId)];
}
